import React, { useState } from "react";
import API_BASE_URL from "../../../../Constants";
import {
  devicePartOptions,
  violatingConditionOptions,
  suggestedRemedyOptions,
  resolutionMethodOptions,
} from "../InspectionList/DeviceVolatingConditions/ELV3DataConfig";

const CATEGORY_LIST = [
  "Inside Car",
  "Outside Hoistway",
  "Top of Car",
  "Machine Room",
  "Pit",
  "Escalator/Moving Walk",
  "Entire Device",
  "All Types",
];

export default function ViolationsForm({
  inspectionId,
  deviceId,
  onRefreshViolations,
  // read-only fields from the parent
  inspectionNumber,
  buildingSite,
  agencyId,
}) {
  // Expand/collapse for device part filter
  const [isFilterExpanded, setIsFilterExpanded] = useState(true);
  // Which categories are chosen
  const [selectedCategories, setSelectedCategories] = useState(["All Types"]);

  // Main violation fields
  const [newViolationDate, setNewViolationDate] = useState("");
  const [newDevicePart, setNewDevicePart] = useState("");
  const [newViolatingCondition, setNewViolatingCondition] = useState("");
  const [newSuggestedRemedy, setNewSuggestedRemedy] = useState("");
  const [newAdditionalComment, setNewAdditionalComment] = useState("");
  const [newEstimatedAmount, setNewEstimatedAmount] = useState("");
  const [newResolutionMethod, setNewResolutionMethod] = useState("");
  const [newItemStatus, setNewItemStatus] = useState("Must Remedy");

  // Category logic
  const handleCategoryChange = (category) => {
    if (category === "All Types") {
      setSelectedCategories(["All Types"]);
    } else {
      setSelectedCategories([category]);
    }
  };

  // Filter device parts by chosen categories
  const filteredDeviceParts = devicePartOptions.filter((partObj) => {
    if (selectedCategories.includes("All Types")) return true;
    return partObj.categories.some((cat) => selectedCategories.includes(cat));
  });

  // POST new violation
  const handleAddViolation = async () => {
    if (!inspectionId || !deviceId) {
      alert("Cannot create a violation without an inspection and device.");
      return;
    }
    const payload = {
      inspectionId: parseInt(inspectionId, 10),
      deviceId: parseInt(deviceId, 10),
      violationDate: newViolationDate || null,
      devicePart: newDevicePart || "",
      violatingCondition: newViolatingCondition || "",
      suggestedRemedy: newSuggestedRemedy || "",
      additionalComment: newAdditionalComment || "",
      resolutionMethod: newResolutionMethod || "",
      itemStatus: newItemStatus || "Must Remedy",
      // read‐only info
      inspectionNumber: inspectionNumber || "",
      buildingSite: buildingSite || "",
      agencyId: agencyId || null,
    };
    if (newEstimatedAmount) {
      payload.estimatedAmount = parseFloat(newEstimatedAmount);
    }

    try {
      const resp = await fetch(`${API_BASE_URL}/api/violations`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!resp.ok) {
        throw new Error(`Failed to create violation. Status: ${resp.status}`);
      }
      alert("New violation created successfully!");
      // Reset form
      setNewViolationDate("");
      setNewDevicePart("");
      setNewViolatingCondition("");
      setNewSuggestedRemedy("");
      setNewAdditionalComment("");
      setNewEstimatedAmount("");
      setNewResolutionMethod("");
      setNewItemStatus("Must Remedy");
      if (onRefreshViolations) onRefreshViolations();
    } catch (err) {
      console.error("Error creating violation:", err);
      alert("Failed to create violation.");
    }
  };

  return (
    <div className="flex flex-col w-full border border-gray-300 rounded-md bg-white">
      {/* 1) (Optional) Inspection Details (read-only) */}
      {(inspectionNumber || buildingSite || agencyId) && (
        <div className="p-3 bg-gray-50 border-b border-gray-200">
          <p className="font-semibold mb-2">Inspection Details</p>
          <p className="text-sm">
            <span className="font-medium">Inspection #:</span>{" "}
            {inspectionNumber || "N/A"}
          </p>
          <p className="text-sm">
            <span className="font-medium">Building Site:</span>{" "}
            {buildingSite || "N/A"}
          </p>
          <p className="text-sm">
            <span className="font-medium">Agency ID:</span> {agencyId ?? "N/A"}
          </p>
        </div>
      )}

      {/* 2) Device Part Filter Header */}
      <div className="p-3 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-sm">Device Part Filter</h3>
          <button
            onClick={() => setIsFilterExpanded(!isFilterExpanded)}
            className="text-blue-600 hover:underline text-sm"
          >
            {isFilterExpanded ? "Hide Filter" : "Show Filter"}
          </button>
        </div>
        {isFilterExpanded && (
          <div className="mt-2 flex flex-wrap gap-2">
            {CATEGORY_LIST.map((category) => {
              const isChecked = selectedCategories.includes(category);
              return (
                <label
                  key={category}
                  className={
                    "inline-flex items-center px-3 py-1 text-sm font-medium rounded cursor-pointer transition " +
                    (isChecked
                      ? "bg-sss-blue-sel text-white border-sss-blue-sel border-2"
                      : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50")
                  }
                >
                  <input
                    type="checkbox"
                    className="hidden"
                    checked={isChecked}
                    onChange={() => handleCategoryChange(category)}
                  />
                  <span>{category}</span>
                </label>
              );
            })}
          </div>
        )}
        {!isFilterExpanded && (
          <div className="mt-1 text-sm text-gray-600 italic">
            Currently: {selectedCategories.join(", ")}
          </div>
        )}
      </div>

      {/* 3) Bottom form => scrollable if tall */}
      <div className="p-3 overflow-y-auto" style={{ maxHeight: "500px" }}>
        {/* MAIN VIOLATION FIELDS */}
        <div className="space-y-3">
          {/* Date */}
          <div>
            <label className="block font-semibold text-sm mb-1">
              Violation Date:
            </label>
            <input
              type="date"
              value={newViolationDate}
              onChange={(e) => setNewViolationDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
            />
          </div>

          {/* Device Part */}
          <div>
            <label className="block font-semibold text-sm mb-1">
              Device Part:
            </label>
            <select
              value={newDevicePart}
              onChange={(e) => setNewDevicePart(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
            >
              <option value="">-- Select Part --</option>
              {filteredDeviceParts.map((partObj) => (
                <option key={partObj.label} value={partObj.label}>
                  {partObj.label}
                </option>
              ))}
            </select>
          </div>

          {/* Condition */}
          <div>
            <label className="block font-semibold text-sm mb-1">
              Violating Condition:
            </label>
            <select
              value={newViolatingCondition}
              onChange={(e) => setNewViolatingCondition(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
            >
              <option value="">-- Select Condition --</option>
              {violatingConditionOptions.map((cond) => (
                <option key={cond} value={cond}>
                  {cond}
                </option>
              ))}
            </select>
          </div>

          {/* Suggested Remedy */}
          <div>
            <label className="block font-semibold text-sm mb-1">
              Suggested Remedy:
            </label>
            <select
              value={newSuggestedRemedy}
              onChange={(e) => setNewSuggestedRemedy(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
            >
              <option value="">-- Select Remedy --</option>
              {suggestedRemedyOptions.map((remedy) => (
                <option key={remedy} value={remedy}>
                  {remedy}
                </option>
              ))}
            </select>
          </div>

          {/* Additional Comment */}
          <div>
            <label className="block font-semibold text-sm mb-1">
              Additional Comment:
            </label>
            <input
              type="text"
              value={newAdditionalComment}
              onChange={(e) => setNewAdditionalComment(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
            />
          </div>

          {/* Estimated Amount */}
          <div>
            <label className="block font-semibold text-sm mb-1">
              Estimated Amount:
            </label>
            <input
              type="number"
              step="0.01"
              value={newEstimatedAmount}
              onChange={(e) => setNewEstimatedAmount(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
            />
          </div>

          {/* Resolution Method */}
          <div>
            <label className="block font-semibold text-sm mb-1">
              Resolution Method:
            </label>
            <select
              value={newResolutionMethod}
              onChange={(e) => setNewResolutionMethod(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
            >
              <option value="">-- Select Method --</option>
              {resolutionMethodOptions.map((method) => (
                <option key={method} value={method}>
                  {method}
                </option>
              ))}
            </select>
          </div>

          {/* Item Status */}
          <div>
            <label className="block font-semibold text-sm mb-1">
              Item Status:
            </label>
            <select
              value={newItemStatus}
              onChange={(e) => setNewItemStatus(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
            >
              <option value="Must Remedy">Must Remedy</option>
              <option value="Remedied">Remedied</option>
            </select>
          </div>
        </div>

        {/* Submit Button */}
        <div className="mt-4">
          <button
            onClick={handleAddViolation}
            className="border border-yellow-500 bg-yellow-500 
                       text-blue-900 font-bold py-2 px-4 rounded 
                       hover:bg-yellow-600 transition-colors"
          >
            Submit
          </button>
        </div>
      </div>
    </div>
  );
}
